import { CustomTypographyVariant } from '#theme';
import { Avatar, AvatarProps, SxProps, Theme, Typography } from '@mui/material';
import React from 'react';

interface Props extends Omit<AvatarProps, 'sx'> {
  imageUrl?: string | null;
  fullName?: string | null;
  size?: 16 | 20 | 28 | 32 | 40 | 80 | 120 | 210;
  variant?: AvatarProps['variant'];
  sx?: SxProps<Theme>;
}

type EmployeeAvatarSize = NonNullable<Props['size']>;

const dimensions = {
  16: '16px',
  20: '20px',
  28: '28px',
  32: '32px',
  40: '40px',
  80: '80px',
  120: '120px',
  210: '210px',
};

const getEmployeeAvatarTypographyVariant = (size: EmployeeAvatarSize): CustomTypographyVariant => {
  switch (size) {
    case 16:
      return 'caption';
    case 20:
    case 28:
      return 'subtitle2';
    case 32:
      return 'subtitle1';
    case 40:
      return 'h5';
    case 80:
      return 'h4';
    case 120:
      return 'h3';
    case 210:
      return 'h1';
  }
};

const getInitials = (fullName: string | null, size: number) => {
  if (!fullName) {
    return '';
  }
  // Filter out empty strings from multiple spaces or leading/trailing spaces
  const names = fullName.split(' ').filter((name) => name.length > 0);

  if (names.length === 0) {
    return '';
  }

  const allInitials = names.map((name) => name[0]).join('');

  if (!allInitials) {
    return '';
  }

  if (size < 28) {
    // use single letter initial on small sizes
    return allInitials[0].toUpperCase();
  }

  // For larger sizes, show first and last initial
  if (allInitials.length === 1) {
    return allInitials[0].toUpperCase();
  }

  return `${allInitials[0]}${allInitials[allInitials.length - 1]}`.toUpperCase();
};

export const EmployeeAvatar: React.FC<Props> = ({
  imageUrl,
  fullName,
  size = 20,
  variant = 'circular',
  sx,
  ...rest
}) => {
  const dimension = dimensions[size];
  const fontSize = getEmployeeAvatarTypographyVariant(size);

  if (!imageUrl && !fullName) {
    console.error('EmployeeAvatar: must have either imageUrl or fullName prop');
    return null;
  }

  if (!imageUrl && fullName) {
    return (
      <Avatar
        variant={variant}
        // eslint-disable-next-line no-restricted-syntax
        sx={{
          height: dimension,
          width: dimension,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          ...sx,
        }}
        {...rest}
      >
        <Typography variant={fontSize}>{getInitials(fullName, size)}</Typography>
      </Avatar>
    );
  }
  return (
    <Avatar
      // eslint-disable-next-line no-restricted-syntax
      sx={{ height: dimension, width: dimension, ...sx }}
      src={imageUrl || ''}
      alt={fullName || ''}
      variant="circular"
      {...rest}
    />
  );
};
